import { designVariables } from "@studio3t/design-variables";
import {
  BadgeSecondarySubtle,
  Icon,
  IconSVG,
} from "@studio3t/generic-components";
import type { ReactNode } from "react";
import styled, { css, useTheme } from "styled-components";
import { STICKY_SHADOW, useScrolledUnder } from "./useScrolledUnder";

const H2 = designVariables.type.heading.h2;
const SINGLE_LINE = designVariables.type.singleLine;
const SEMANTIC = designVariables.ui.semantic;
const BREADCRUMBS = designVariables.ui.breadcrumbs;
const BUTTON = designVariables.ui.button;
const LIST = designVariables.ui.list;

// Keeps the title row + breadcrumb pinned to the top of the scrolling page
// (PageContainer in App.tsx) so they stay visible while the cards/list scroll.
// The page background + bottom padding live here (rather than as a margin on the
// breadcrumb row) so scrolling content can't peek through under the pinned
// header. z-index sits above the page content but below portalled overlays.
const StickyHeader = styled.div<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  z-index: 10;
  background: ${(props) => props.theme.semanticAppBackground};
  /* Bleed across PageContainer's horizontal padding so the pinned header —
     and the shadow it casts — runs the full width of the content area with
     no gaps at the sides. */
  margin: 0 -${SEMANTIC.spacingl}px;
  padding-left: ${SEMANTIC.spacingl}px;
  padding-right: ${SEMANTIC.spacingl}px;
  /* The pinned-header shadow appears once content scrolls beneath
     (Figma 15544-191515); at rest the header sits flush with the page. */
  transition: box-shadow 0.15s ease;
  box-shadow: ${(p) => (p.$scrolled ? STICKY_SHADOW : "none")};
  /* Bottom padding is the gap to the content below (replaces the breadcrumb
     row's old margin so scrolling content can't peek through it). */
  padding-bottom: ${SEMANTIC.spacings}px;

  /* PageContainer (App.tsx) has top padding; when the header is pinned, content
     would otherwise scroll through that strip above it. This fills the strip
     with the page background, tracking the header as it sticks. */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    bottom: 100%;
    height: ${SEMANTIC.spacings}px;
    background: ${(props) => props.theme.semanticAppBackground};
  }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${SEMANTIC.spacings}px;
  margin-bottom: ${SEMANTIC.spacings}px;
  flex-shrink: 0;
  /* Figma Page/title keeps a 32px right slot even without an action
     (13349-39129), so the row height never collapses below the M-button
     height on action-less pages. */
  min-height: 32px;
`;

const TitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${SEMANTIC.spacings}px;
`;

const PageTitle = styled.h1`
  font-size: ${H2.fontSize}px;
  font-weight: ${H2.fontWeight};
  line-height: ${H2.lineHeight}px;
  margin: 0;
  color: ${(props) => props.theme.semanticTextHeading};
`;

// The breadcrumb row sits where the list's label sits (Figma 13017-5470), so the
// header + breadcrumb stay in place when navigating into a detail page. Figma
// breadcrumb-item row height; no breadcrumb height token exists.
const BreadcrumbRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: ${SEMANTIC.spacings}px;
  min-height: 32px;
  flex-shrink: 0;
`;

const BreadcrumbTrail = styled.nav`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${BREADCRUMBS.gap}px;
  min-width: 0;
`;

// Shared breadcrumb-item metrics so a clickable crumb and the current crumb are
// identical in size/position — only colour/interactivity differ. Keeps the
// crumb from jumping when navigating between the list and a single item.
// Per Figma 13721-25259: 32px-tall item with horizontal padding only (no
// vertical padding), labelled in the medium single-line style.
const breadcrumbItem = css`
  display: inline-flex;
  align-items: center;
  min-height: 32px;
  padding: 0 ${BREADCRUMBS.item.paddingw}px;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
`;

// A non-clickable crumb (the current location) — Figma breadcrumbs/item default.
// Unlike the parent-link crumbs (short, fixed labels), this can carry a long
// name, so let it wrap on a narrow header instead of overflowing the trail.
// min-width:0 lets it shrink inside the inline-flex group.
export const BreadcrumbCurrent = styled.span`
  ${breadcrumbItem}
  color: ${(props) => props.theme.breadCrumbItemDefaultText};
  white-space: normal;
  overflow-wrap: anywhere;
  min-width: 0;
  /* Grow to fill its line inside the wrapping group: when the name + badge
     don't fit, the name keeps the line (wrapping its own text if needed) and
     the badge falls to the next row, rather than the name shrinking to squeeze
     the badge alongside. */
  flex: 1 1 auto;
`;

// A clickable parent crumb — identical metrics/colour to the current crumb (so
// it doesn't jump on navigation); the design-system hover pill signals it's a
// link.
const BreadcrumbCrumbButton = styled.button`
  ${breadcrumbItem}
  border: none;
  background: none;
  cursor: pointer;
  border-radius: ${BREADCRUMBS.corner}px;
  color: ${(props) => props.theme.breadCrumbItemDefaultText};

  &:hover {
    background: ${(props) => props.theme.breadCrumbItemDefaultHoverBackground};
    color: ${(props) => props.theme.breadCrumbItemDefaultHoverText};
  }

  &:active {
    background: none;
    color: ${(props) => props.theme.breadCrumbItemClickableText};
  }
`;

// The chevron sits centred in a list-item "type" box (Figma 13721-25259,
// List/item/type 20px): the box, not the glyph, is what the neighbouring
// crumbs space against.
const SeparatorWrap = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${LIST.item.wrap}px;
  height: ${LIST.item.wrap}px;
`;

export function BreadcrumbSeparator() {
  const theme = useTheme();
  return (
    <SeparatorWrap aria-hidden="true">
      {/* Chevron points right by default (per Figma 13017-6430), so no rotation. */}
      <IconSVG
        icon={Icon.Chevron}
        size={LIST.item.chevron}
        colors={{ default: theme.breadCrumbItemDefaultText }}
      />
    </SeparatorWrap>
  );
}

// Keeps the crumbs inside it on one line so they wrap as a single unit (e.g. a
// label + its trailing chevron). Pass $wrap for the current crumb so its status
// badge can drop onto its own line below the (long) name when the header is too
// narrow to hold both side by side.
export const BreadcrumbGroup = styled.span<{ $gap?: number; $wrap?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${(props) => props.$gap ?? BREADCRUMBS.gap}px;
  white-space: ${(props) => (props.$wrap ? "normal" : "nowrap")};
  ${(props) =>
    props.$wrap &&
    css`
      flex-wrap: wrap;
      row-gap: ${SEMANTIC.spacings}px;
    `}
`;

// The current crumb with its status badge (Figma 13721-25259 "Left"): the
// breadcrumb item gap between the two, wrapping so the badge can drop under
// a long name.
export const BreadcrumbCurrentGroup = styled(BreadcrumbGroup).attrs({
  $gap: BREADCRUMBS.item.gap,
  $wrap: true,
})``;

// The pipeline crumb's leading Trace icon (Figma 13721-18157 clickable /
// 13943-171557 current). Its color says whether the crumb is a link: the
// crumb label's own color on clickable crumbs, the subtle button's
// disabled gray (`muted`) on the current, non-clickable one.
export function PipelineCrumbIcon({ muted }: { muted?: boolean }) {
  const theme = useTheme();
  return (
    <CrumbIconWrap aria-hidden="true">
      <IconSVG
        icon={Icon.Trace}
        size={BREADCRUMBS.item.icon}
        colors={{
          default: muted
            ? theme.buttonSubtleDisabledText
            : theme.breadCrumbItemDefaultText,
        }}
      />
    </CrumbIconWrap>
  );
}

// Same recipe as ServiceDetail's CrumbLeadingIcon: the crumb is a medium
// button (Figma 13721-25259), so icon and label sit a button gap apart,
// centred on the text line.
const CrumbIconWrap = styled.span`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  margin-right: ${BUTTON.gap}px;
`;

export function BreadcrumbLink({
  text,
  onClick,
}: {
  text: string;
  onClick: () => void;
}) {
  return (
    <BreadcrumbGroup $gap={0}>
      <BreadcrumbCrumbButton type="button" onClick={onClick}>
        {text}
      </BreadcrumbCrumbButton>
      <BreadcrumbSeparator />
    </BreadcrumbGroup>
  );
}

// Shared page header + breadcrumb row for list/detail screens (Pipelines list,
// a single pipeline, Services list, …). The title row (title + count badge +
// primary action) is identical across screens; only the title, the action
// button, and the breadcrumb trail (with optional right-aligned actions) change.
export function ListPageHeader({
  title,
  count,
  action,
  breadcrumb,
  actions,
}: {
  title: string;
  count?: number;
  action?: ReactNode;
  /** Omit to skip the breadcrumb row entirely (e.g. /logs). */
  breadcrumb?: ReactNode;
  actions?: ReactNode;
}) {
  const { ref, scrolled } = useScrolledUnder<HTMLDivElement>();

  return (
    <StickyHeader ref={ref} $scrolled={scrolled} data-sticky-page-header>
      <PageHeader>
        <TitleGroup>
          <PageTitle>{title}</PageTitle>
          {count != null && (
            <BadgeSecondarySubtle>{count}</BadgeSecondarySubtle>
          )}
        </TitleGroup>
        {action}
      </PageHeader>
      {(breadcrumb || actions) && (
        <BreadcrumbRow>
          <BreadcrumbTrail aria-label="Breadcrumb">
            {breadcrumb}
          </BreadcrumbTrail>
          {actions}
        </BreadcrumbRow>
      )}
    </StickyHeader>
  );
}
